import { useMutation } from '@tanstack/react-query'
import head from 'lodash/head'

import { queryClient } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'

import useRecentOrderWarning, { RECENT_ORDER_DAYS } from '@hooks/useRecentOrderWarning'
import { usePatientPrescriptions } from '@pages/Prescriptions/PatientPrescriptions'

/**
 * Get the change healthcare iframe src url.
 */
export function useGetChangeUrl(providerId) {
  return useMutation({
    mutationFn: (data) => API.providers.id(providerId).prescriptions.url(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['change-url', providerId] }),
  })
}

/**
 * Warns the provider when the patient already has a prescription written within the last 7 days.
 *
 * The endpoint sorts by `prescriptions.created_at` and defaults to ascending, so 'desc' is passed
 * explicitly to get the most recent one.
 *
 * @param {Object} patient - The patient the prescription will be associated with
 * @param {Object} options
 * @param {Function} options.onConfirm - Called when the provider acknowledges the warning
 * @param {Function} options.onCancel - Called when the provider decides not to write another prescription
 */
export function useRecentPrescriptionWarning(patient, { onConfirm, onCancel }) {
  const { data: rx, isPending } = usePatientPrescriptions(
    patient?.id,
    { limit: 1, offset: 0, order: 'desc' },
    { enabled: Boolean(patient?.id), select: head }
  )

  useRecentOrderWarning({
    isPending,
    createdAt: rx?.createdAt,
    title: 'Recent Prescription Found',
    description: `A prescription was written for this patient within the last ${RECENT_ORDER_DAYS} days. Please review the patient's Prescription history before continuing.`,
    onConfirm,
    onCancel,
  })
}

/**
 * Tell BE to sync prescriptions with change healthcare.
 */
export function useSyncPrescriptions(providerId) {
  return useMutation({
    mutationFn: (data) => API.providers.id(providerId).prescriptions.sync(data),
  })
}
